import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {Menu, Divider, MenuItem, Box, Typography, IconButton} from '@mui/material';
import React, {useState} from 'react';

import {DlMuiDynamicIcon} from './DlMuiDynamicIcon';
import {LightIconButton} from './LightIconButton';

interface IconMenuItemProps {
  iconName: string;
  isSelected?: boolean;
  onClick: () => void;
  iconTitle?: string;
  size?: number;
  useFixedViewBox?: number | string;
  preserveAspectRatio?: string;
  iconColorStyle?: string;
}

const IconMenuItem: React.FC<IconMenuItemProps> = ({
  iconName,
  isSelected = false,
  onClick,
  iconTitle,
  size = 22,
  useFixedViewBox,
  preserveAspectRatio,
  iconColorStyle,
}) => {
  return (
    <MenuItem
      onClick={onClick}
      selected={isSelected}
      sx={{
        'display': 'flex',
        'alignItems': 'center',
        'justifyContent': 'center',
        'py': 1,
        'minHeight': 40,
        '&:hover': {backgroundColor: 'action.hover'},
      }}
    >
      <Box
        component='span'
        sx={{
          width: size + 8,
          height: size + 8,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid',
          borderColor: 'background.paper',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
          flexShrink: 0,
        }}
      >
        <DlMuiDynamicIcon
          iconName={iconName}
          iconTitle={iconTitle}
          noButtonStyles
          useFixedViewBox={useFixedViewBox}
          preserveAspectRatio={preserveAspectRatio}
          style={{color: iconColorStyle}}
          svgIconStyle={{width: size, height: size, color: iconColorStyle}}
          sx={{p: 0, m: 0}}
        />
      </Box>
    </MenuItem>
  );
};

export interface LightIconPickerProps {
  iconNames: string[];
  onIconSelected: (iconName: string | null) => void;
  selectedIconName?: string | null;
  disabled?: boolean;
  showEmptyOption?: boolean;
  iconColorStyle?: string;
}

export const LightIconPicker: React.FC<LightIconPickerProps> = ({
  iconNames,
  onIconSelected,
  selectedIconName = null,
  disabled = false,
  showEmptyOption = true,
  iconColorStyle,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (iconName: string | null) => {
    onIconSelected(iconName);
    handleClose();
  };

  return (
    <div
      style={{width: '50px', cursor: 'pointer'}}
      onClick={(e) => {
        if (disabled) return;
        const target = anchorEl ? null : e.currentTarget;
        setAnchorEl(target);
      }}
    >
      {selectedIconName ? (
        <LightIconButton
          iconName={selectedIconName}
          style={{width: '100%', height: '100%', color: iconColorStyle}}
          wrapperStyle={{padding: 0, pointerEvents: 'none'}}
        />
      ) : (
        <span style={{display: 'inline-block', width: '30px', height: '22px'}} />
      )}
      <IconButton style={{padding: 0}} size='small' disabled={disabled}>
        <ArrowDropDownIcon fontSize='small' />
      </IconButton>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
        transformOrigin={{vertical: 'top', horizontal: 'left'}}
        PaperProps={{sx: {minWidth: 60, maxWidth: 140}}}
        onBackdropClick={handleClose}
      >
        {showEmptyOption && (
          <>
            <MenuItem
              selected={selectedIconName === null}
              onClick={() => handleSelect(null)}
              sx={{
                'display': 'flex',
                'justifyContent': 'center',
                'py': 1,
                'minHeight': 40,
                '&:hover': {backgroundColor: 'action.hover'},
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                -
              </Typography>
            </MenuItem>

            <Divider sx={{my: 0.5}} />
          </>
        )}

        {iconNames.map((name) => (
          <IconMenuItem
            key={name}
            iconName={name}
            isSelected={selectedIconName === name}
            onClick={() => handleSelect(name)}
            iconColorStyle={iconColorStyle}
          />
        ))}
      </Menu>
    </div>
  );
};
